/**
 * CGM-OPERATIONAL-CONTROL-R3 — bounded telemetry history read for one node.
 *
 * Reads the existing H2 API (GET /iot/nodes/{id}/telemetry?hours=&limit=).
 * Fail-soft for transport errors, and fail-closed for a saturated result limit:
 * a possibly truncated series is never exposed as a complete history because
 * downstream integrations (notably cumulative water) would undercount it.
 */

import { useEffect, useState } from 'react';
import { iotApi } from '../lib/iotApi';
import type { TelemetryPoint } from '../lib/operationalOverview';

export interface NodeHistoryState {
  series: TelemetryPoint[];
  loading: boolean;
  error: boolean;
  /** True when the API returned the full request limit, so older samples may exist. */
  truncated: boolean;
}

interface ScopedNodeHistoryState extends NodeHistoryState {
  /** Exact request scope that produced this state. */
  scopeKey: string | null;
}

const DEFAULT_HISTORY_LIMIT = 2000;
const EXTENDED_HISTORY_LIMIT = 5000;
const HISTORY_READ_TIMEOUT_MS = 30_000;

// Bound both fetching and JSON decoding; a stalled read must leave loading.
function readHistory(path: string): { promise: Promise<TelemetryPoint[]>; cancel: () => void } {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  let cancel!: () => void;
  const timeout = new Promise<never>((_, reject) => {
    cancel = () => {
      controller.abort();
      reject(new Error('History read cancelled or timed out'));
    };
    timer = setTimeout(cancel, HISTORY_READ_TIMEOUT_MS);
  });
  return {
    promise: Promise.race([iotApi.getJson<TelemetryPoint[]>(path, controller.signal), timeout])
      .finally(() => clearTimeout(timer)),
    cancel,
  };
}

const emptyState = (loading = false): NodeHistoryState => ({
  series: [],
  loading,
  error: false,
  truncated: false,
});

const historyScopeKey = (nodeId: string | null, hours: number): string | null =>
  nodeId && Number.isFinite(hours) && hours > 0 ? `${nodeId}\u0000${hours}` : null;

// Shared only by mounted authenticated consumers; no history survives the last
// subscriber. Include refresh generation so a new sample starts a fresh read.
const sharedReads = new Map<string, ReturnType<typeof readHistory> & { users: number }>();

export function useNodeHistory(nodeId: string | null, hours: number, refreshKey?: string, authScope?: string): NodeHistoryState {
  // Seven days at five-minute cadence already contains 2,016 samples.
  const HISTORY_LIMIT = hours >= 168 ? EXTENDED_HISTORY_LIMIT : DEFAULT_HISTORY_LIMIT;
  const nodeScope = historyScopeKey(nodeId, hours);
  const requestedScopeKey = nodeScope ? JSON.stringify([authScope ?? null, nodeScope]) : null;
  const [state, setState] = useState<ScopedNodeHistoryState>({
    scopeKey: null,
    ...emptyState(false),
  });
  const [refresh, setRefresh] = useState({ scope: requestedScopeKey, key: refreshKey });
  const effectiveRefresh = refresh.scope === requestedScopeKey ? refresh.key : refreshKey;
  // Coalesce incoming uplinks while reading. Cancelling on every uplink can
  // starve a slow history request forever and hide its completed cycle.
  useEffect(() => {
    if (state.scopeKey !== requestedScopeKey || !state.loading) {
      setRefresh(previous => previous.scope === requestedScopeKey && previous.key === refreshKey
        ? previous : { scope: requestedScopeKey, key: refreshKey });
    }
  }, [requestedScopeKey, refreshKey, state.scopeKey, state.loading]);

  useEffect(() => {
    if (!requestedScopeKey || !nodeId) {
      setState({ scopeKey: null, ...emptyState(false) });
      return;
    }

    let active = true;
    setState((previous) => previous.scopeKey === requestedScopeKey
      ? { ...previous, loading: true }
      : { scopeKey: requestedScopeKey, ...emptyState(true) });

    const cacheKey = JSON.stringify([requestedScopeKey, effectiveRefresh ?? null]);
    let shared = authScope ? sharedReads.get(cacheKey) : undefined;
    const operation = shared ?? readHistory(
      `/iot/nodes/${encodeURIComponent(nodeId)}/telemetry?hours=${hours}&limit=${HISTORY_LIMIT}`,
    );
    if (authScope && !shared && sharedReads.size < 64) {
      shared = { ...operation, users: 0 };
      sharedReads.set(cacheKey, shared);
    }
    if (shared) shared.users += 1;
    operation.promise.then((rows) => {
        if (!active) return;
        const safeRows = Array.isArray(rows) ? rows : [];
        const saturated = safeRows.length >= HISTORY_LIMIT;
        setState({
          scopeKey: requestedScopeKey,
          series: saturated ? [] : safeRows,
          loading: false,
          error: saturated,
          truncated: saturated,
        });
      })
      .catch(() => {
        if (!active) return;
        setState((previous) => ({
          ...(previous.scopeKey === requestedScopeKey ? previous : emptyState()),
          scopeKey: requestedScopeKey,
          loading: false,
          error: true,
        }));
      });

    return () => {
      active = false;
      if (!shared) operation.cancel();
      else if (--shared.users === 0 && sharedReads.get(cacheKey) === shared) {
        sharedReads.delete(cacheKey);
        shared.cancel();
      }
    };
  }, [nodeId, hours, requestedScopeKey, effectiveRefresh, authScope, HISTORY_LIMIT]);

  // Scope isolation is enforced during render, before passive effects run. If
  // props changed since the stored state was produced, consumers immediately
  // see an empty/loading projection and can neither paint nor export old rows.
  if (!requestedScopeKey) return emptyState(false);
  if (state.scopeKey !== requestedScopeKey) return emptyState(true);

  return {
    series: state.series,
    loading: state.loading,
    error: state.error,
    truncated: state.truncated,
  };
}
